import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLoaderData, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IVehicle } from "dataModels/contract";
import { 
    FormMode, 
    Direction, 
    EntityType, 
    RoutePath, 
    VehicleType, 
    getVehicleFileInputConfig,
    IFileInputConfig, 
    DATE_FORMAT, 
    DocumentType, 
    Affirmation, 
    DsLayout,
    ResponseStatus,
    ResponseCode,
    IAppUserProfile,
    AppUserRole,
    ULTRA_LATE_DATE
} from "shared/constants";
import { ISelectLabel } from "@ds/ds-snam/dist/models/select";
import { DatePicker, Button, Select } from "@ds/ds-snam/react";
import { InputAttach } from "@ds/ds-snam/react";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { getTransporterDomain, postVehicle, putVehicle } from "net/contracts";
import { Container, Row, Col } from "react-grid-system";
import SystemError from "components/SystemError";
import Stepper, { StepperType } from "views/contractView/components/newElementFormStepper";
import VehicleFormDataInput from "./vehicleFormDataInput";
import { DSInput, DSSelectList, DSFile } from "@ds/ds-snam/dist/index";
import _ from "lodash";
import { v4 as uuid } from "uuid";
import DSFileInput from "components/DSFileInput";
import { FileStatusEnum, IFileUpload } from "dataModels/fileInput";
import { IDocument } from "dataModels/contract";
import moment from "moment";
import "views/contractView/styles/newElementFormStyles.css";
import VehicleFormReview from "./vehicleFormReview";
import { INotificationAction, NotificationsStatusType } from "dataModels/stateModels/notification";
import { showNotification, hideNotification } from "state/reducer/notificationSlice";
import { dsSelectHandler } from "shared/utils/dsComponentUtils";
import { RootState } from "state/appStore";
import { AppUser } from "ooAuth2/appUser";
import useAppUser from "hooks/useAppUser";

const VehicleForm: React.FC = (): JSX.Element => {
    //Vehicle id - When access to retrieve and edit driver existing information, otherwise undefined
    const { id } = useParams();
    //Loaded vehicle data when in edit mode
    const vehicleData: IVehicle | undefined = useLoaderData() as IVehicle;


    const { t, i18n } = useTranslation(['contract', 'app']);
    const navigate = useNavigate();
    const getDefaultFormData = (vehicleType: VehicleType = VehicleType.TRUCK, transporterId = ""): IVehicle => {
        const defaultData: IVehicle = {
            vehicleId: "",
            transporterId: transporterId,
            type: vehicleType,//default val or param to reset
            licencePlate: "",
            isoContainerCode: "",
            emptyWeight: 0,
            volume: 0,
            designPressure: 0,
            holdingTime: 0,
            maxFillingDegree: 0,
            maxFillingWeight: 0,
            vacuumIsolation: "0",
            manufacturer: "",
            yearManufacture: "",
            twoLevelsIndicators: "0",
            groundingConnection: "0",
            flameArrest: Affirmation.NO,//"0",
            loadingSide: Direction.RIGHT,
            documents: Array(9).fill({
                type: '',
                name: '',
                size: 0,
                content: '',
                expire: null
            }),
            status: 2,// stato del veicolo: 2 da approvare, 3 approvato, 4 non approvato
            statusComplete:5

        }
        return defaultData;
    }
    
    //eslint-disable-next-line
    const [formMode, setFormMode] = useState<number>(id ? FormMode.EDIT : FormMode.NEW);
    const [formStep, setFormStep] = useState<number>(formMode === FormMode.NEW ? 0 : 1);
    const [transporterList, setTransporterList] = useState<ISelectLabel[]>([]);
    const [formData, setFormData] = useState<IVehicle>(formMode === FormMode.NEW ? getDefaultFormData(VehicleType.TRUCK, "") : vehicleData);
    const [formError, setFormError] = useState<boolean>(false);
    const [vehicleTypeOptions, setVehicleTypeOptions] = useState<ISelectLabel[] | undefined>();
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    const dispatch = useDispatch();
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    const appUserProfile: IAppUserProfile | undefined = appUser?.userProfile;

    const loadTransporters = useCallback(() => {
        dispatch(requestPending());
        getTransporterDomain(EntityType.TRANSPORTER)
            .then((response: ISelectLabel[]) => {
                setTransporterList(response)
            })
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [dispatch]);


    useEffect(() => {
        if (formMode === FormMode.NEW) {
            //Selector for transporter available only in new form mode
            loadTransporters();
        }
        //eslint-disable-next-line
    }, [loadTransporters]);

    useEffect(() => {
        if (formMode === FormMode.NEW) {
            //reset fields on type change - on edit mode type select is disabled
            const emptyFormData = getDefaultFormData(formData.type, formData.transporterId);
            if(formData.type===VehicleType.ISO_CONTAINER || formData.type===VehicleType.TANKER){ 
                (emptyFormData['yearManufacture' as keyof IVehicle] as string) = new Date().getFullYear().toString();
            }
            setFormData(emptyFormData);
        }
    }, [formData.type, formMode])

    useEffect(() => {
        //Select type
        let options: ISelectLabel[] = [];
        _.forIn(VehicleType, (value: number, key: string) => {
            if (isNaN(Number(key))) {
                //console.log(value, key)
                /*
                0 'TRUCK'
                1 'TANKER'
                2 'ISO_CONTAINER'
                3 'FLOOR'
                */
                //In case of edit, it needs to be preselected with actual form data, false otherwise
                const isSelected: boolean = value === formData.type;
                const label: string = t('vehicle.vehicleList.vehicleType.' + value.toString());
                options.push({ id: value.toString(), label: label, value: value.toString(), selected: isSelected });
            }
        })
        setVehicleTypeOptions(options);

        //eslint-disable-next-line
    }, [i18n.language]);

    const onVehicleTypeChange = (inputEvent: Event) => {
        const selection: ISelectLabel = (inputEvent as CustomEvent).detail.option;
        dsSelectHandler(vehicleTypeOptions!, selection);
        let currentFormData = { ...formData };
        currentFormData.type = Number(selection?.value! as unknown) as VehicleType;
        setFormData(currentFormData);
    }

    const onLoadingSideChange = (inputEvent: Event) => {
        const selection: ISelectLabel = (inputEvent as CustomEvent).detail.option;
        let currentFormData = { ...formData };
        currentFormData.loadingSide = Number(selection?.value! as unknown) as Direction;
        setFormData(currentFormData);
    }

    const onTextInputChange = (inputEvent: Event) => {
        //console.log((inputEvent!.target! as HTMLElement).getAttribute("is-number-variant"));
        const fieldName = (inputEvent as CustomEvent).detail.input.name;
        let newValue = (inputEvent as CustomEvent).detail.input.value;
        if((inputEvent!.target! as HTMLElement).getAttribute("is-number-variant")==="true"){
            newValue = Number(newValue);
        }
        let currentData: IVehicle = formData;
        (currentData[fieldName as keyof IVehicle] as string) = newValue;//(inputEvent as CustomEvent).detail.input.value;
        setFormData(currentData);
        //console.log(formData)
    }

    const onManufactoringYearInputChange = (inputEvent: Event) => {
        const fieldName = 'yearManufacture';
        let newValue = (inputEvent as CustomEvent).detail.dateSelection;
        let currentData: IVehicle = formData;
        (currentData[fieldName as keyof IVehicle] as string) = newValue;//(inputEvent as CustomEvent).detail.input.value;
        setFormData(currentData);
    }

    const onRadioChange = (event: Event, fieldName: string) => {
        //console.log((event as CustomEvent).detail.selectedRadio);
        const selection: string = (event as CustomEvent).detail.selectedRadio.value;
        let currentFormData = { ...formData };
        (currentFormData[fieldName as keyof IVehicle] as string) = selection;//(selection === "0") ? true : false;
        setFormData(currentFormData);
    }

    const prevStep = () => {
        const nextStep = formStep - 1;
        setFormStep(nextStep);
    }
    const nextStep = () => {
        const nextStep = formStep + 1;
        setFormStep(nextStep);
    }

    /*const validateStep = (): number => {
        let errors: number = 0;
        if (formStep === 1) {
            const excludeList: string[] = [
                "maxFillingDegree",
                "maxFillingWeight"
            ]
            const mandatoryFields: string[] = _.filter(Object.keys(formData), (fieldName: string) => {
                return excludeList.indexOf(fieldName) === -1;
            });

            mandatoryFields.forEach((key: string) => {
                const elem: DSInput | null = document.querySelector("[input-id='" + key + "']");
                if (elem) {
                    //console.log(elem, elem.tagName)
                    const content = formData[key as keyof IVehicle]!;
                    const koCheck: boolean = content === "" || Number(content) <= 0;

                    elem.inputStatus = koCheck ? FileStatusEnum.ERROR : FileStatusEnum.DEFAULT;

                    if (koCheck)
                        errors++;
                }
            });
        } else if (formStep >= 2 || formStep <= 4) {
            _.filter(fileInputConfig, { 'step': formStep }).forEach((configObj) => {
                const docId: string = 'doc' + configObj.id;
                const elem: DSFile | null = document.querySelector("[input-id='" + docId + "']");
                if (elem) {
                    const koCheck: boolean = formData.documents[configObj.id].content.length === 0;
                    elem.inputStatus = FileStatusEnum.ERROR
                    if (koCheck) {
                        errors++;
                        elem.inputStatus = FileStatusEnum.ERROR;
                    }
                }

            });
        }
        return errors;
    }*/

    const validateStep = (): number => {
            let errors: number = 0;
        
            if (formStep === 1) {
                const excludeList: string[] = ["maxFillingDegree", "maxFillingWeight"];
                const mandatoryFields: string[] = _.filter(Object.keys(formData), (fieldName: string) => {
                    return excludeList.indexOf(fieldName) === -1;
                });
        
                mandatoryFields.forEach((key: string) => {
                    const elem: DSInput | null = document.querySelector("[input-id='" + key + "']");
                    if (elem) {
                        const content = formData[key as keyof IVehicle]!;
                        const koCheck: boolean = content === "" || Number(content) <= 0;
        
                        elem.inputStatus = koCheck ? FileStatusEnum.ERROR : FileStatusEnum.DEFAULT;
        
                        if (koCheck) errors++;
                    }
                });
            } else if (formStep >= 2 || formStep <= 4) {
                
                fileInputConfig.forEach((configObj) => {
                    if ( /*configObj.id!== 8 && */ configObj.step === formStep) {

                        const docId: string = 'doc' + configObj.id;
                        const elem: DSFile | null = document.querySelector("[input-id='" + docId + "']");

                        //console.log(docId, formData.documents[configObj.id]?.type, formData.type);
                        /**
                        Tractor-FlatBed type 0 e 3
                            doc0 vehicle resgitration * //DOCUMENTS
                            doc1 tecnical check * //DOCUMENTS
                            doc3 GA drawing //DOCUMENTS
                            doc5 insurance certificate * //PROCEDURES
                            doc6 adr * //PROCEDURES
                            doc7 testCertificate //PROCEDURES
                            doc8 flame arrest //IL FLATBED NON LO HA, SEMITRAILER E ISOCONTAINER NON LO HANNO

                        SemiTrailer-IsoContainer type 1 e 2
                            doc0 vehicle resgitration * //DOCUMENTS //ISOCONTAINER NON LO HA
                            doc1 tecnical check * //DOCUMENTS //ISOCONTAINER NON LO HA
                            doc2 pidpdf * //DOCUMENTS
                            doc3 GA drawing * //DOCUMENTS
                            doc4 loading * //PROCEDURES
                            doc5 insurance certificate * //PROCEDURES //ISOCONTAINER NON LO HA
                            doc6 adr * //PROCEDURES
                            doc7 testCertificate * //PROCEDURES
                        */
        
                        if (elem) {
                            const hasContent = formData.documents[configObj.id]?.content.length > 0;
                            const shouldHaveExpiration = configObj.expireRequired;
                            const hasValidExpiration = !shouldHaveExpiration || (formData.documents[configObj.id]?.expire || 0) > 0;

                            if (!hasContent || !hasValidExpiration) {
                                if(formData.type===0||formData.type===3){
                                    if(
                                        (docId!=='doc3' && //documenti facoltativi
                                        docId!=='doc7' && 
                                        docId!=='doc8')
                                    ){
                                        elem.inputStatus = FileStatusEnum.ERROR;
                                        errors++;
                                    }
                                }else{
                                    elem.inputStatus = FileStatusEnum.ERROR;
                                    errors++;
                                }
                            } else {
                                elem.inputStatus = FileStatusEnum.UPLOADED;
                            }
                        }

                    }
                });

            }
        
            return errors;
    };
    const saveFormData = useCallback(() => {
        const translationField: string = formMode === FormMode.NEW ? "new" : "edit";
        const request = formMode === FormMode.EDIT ? putVehicle : postVehicle;
        let popUpAction: INotificationAction;
        

        if (formData.type === VehicleType.TRUCK || formData.type === VehicleType.FLOOR) {
            const defaultsForTruck: Record<number, any> = {
                3: {
                    type: DocumentType.GA_DRAWING,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: null
                },
                7: {
                    type: DocumentType.TEST_CERTIFICATE,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: ULTRA_LATE_DATE
                },
                8: {
                    type: DocumentType.FLAME_ARREST,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: null
                }
            };

            const defaultsForFloor: Record<number, any> = {
                3: {
                    type: DocumentType.GA_DRAWING,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: null
                },
                7: {
                    type: DocumentType.TEST_CERTIFICATE,
                    name: DocumentType.EMPTY_FILE,
                    size: 0,
                    content: '',
                    expire: ULTRA_LATE_DATE
                }
            };

            const defaults = formData.type === VehicleType.TRUCK ? defaultsForTruck : defaultsForFloor;

            formData.documents = formData.documents.map((doc, index) => {
                if (doc.type && doc.type.trim() !== '') return doc; // già valorizzato, non tocco
                if (defaults[index]) return defaults[index]; // se c’è default per quell’indice, lo applico
                return doc; // altrimenti lo lascio com'è
            });
        }

        dispatch(requestPending());
        request(formData)
            .then((response) => {
                const popUpTitle:string = response.data.status===ResponseStatus.ERROR ? 'Error':(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT? '422': t(`vehicle.vehicleForm.successToast.${translationField}.title`) );
                const popUpDescription:string = response.data.status===ResponseStatus.ERROR||response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?response.data.message:t(`vehicle.vehicleForm.successToast.${translationField}.subTitle`);
                const popUpStatus:NotificationsStatusType = response.data.status===ResponseStatus.ERROR?"error":(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT?"warning":"confirm");
                             
                popUpAction = {
                    show: true,
                    title: popUpTitle,
                    description: popUpDescription,
                    status: popUpStatus,
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                if(formMode===FormMode.NEW){
                    navigate(RoutePath.CONTRACTS_VEHICLE_INFO);
                }else{//edit
                    navigate(RoutePath.CONTRACTS_VEHICLE_INFO+"/"+id);
                }
            })
            .catch((error) => {
                console.log(error);
                              popUpAction = {
                    show: true,
                    title: "Error",
                    description: 
                    (
                        error.response.data.message.includes("FK_Reservations_LastTankerTruckId_LastLoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservation_TankerTruckId_LoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservations_LastIsoContainerId_LastLoadingSideTypeId") ||
                        error.response.data.message.includes("FK_Reservation_IsoContainerId_LoadingSideTypeId")
                    )
                    ?t('vehicle.vehicleForm.pageHeaders.edit.3'):error.message,
                    status: "error",
                    onClose: (event: Event) => {
                        dispatch(hideNotification());
                    }
                };
                dispatch(showNotification(popUpAction));
                setFormError(true);//error step
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }, [formData, dispatch, navigate, t]);

    const fileInputConfig: IFileInputConfig[] = getVehicleFileInputConfig(formData.type, formData.flameArrest);

    const getFileInputElements = () => {
        //there's a bug on ds component if I filter array, better to reander them all and set display noine with css
        //return _.filter(fileInputConfig, { 'step': formStep }).map((fileConfigObj) => { 

        return fileInputConfig.map((fileConfigObj: IFileInputConfig) => {
            return (<div key={uuid()}>
                <Row>
                    <Col>
                        <div className="fileInputWrapper" style={{ display: formStep === fileConfigObj.step ? "block" : "none",marginTop:'12px' }}>
                            <DSFileInput
                                title={
                                    //t('vehicle.vehicleForm.sections.fileInput.title.' + fileConfigObj.id)
                                    (
                                        ((formData.type===VehicleType.TRUCK||formData.type===VehicleType.FLOOR)&&(fileConfigObj.id === 8||fileConfigObj.id ===3||fileConfigObj.id === 7))
                                    )
                                        ? t('vehicle.vehicleForm.sections.fileInput.title.' + fileConfigObj.id)
                                        : t('vehicle.vehicleForm.sections.fileInput.title.' + fileConfigObj.id) + " *" 
                                }
                                inputLabel={formData.documents[fileConfigObj.id].content.length ? t('dsComponents.fileInput.uploadedStatus.title', { ns: 'app' }) : t('dsComponents.fileInput.defaultStatus.title', { ns: 'app' })}
                                buttonText={t('dsComponents.fileInput.defaultStatus.subTitle', { ns: 'app' })}
                                id={"doc" + fileConfigObj.id}
                                accept="application/pdf, image/jpg, image/jpeg"
                                maxSize={10000000}
                                defaultStatus={formData.documents[fileConfigObj.id].content.length > 0 ? FileStatusEnum.UPLOADED : FileStatusEnum.DEFAULT}
                                onFileUpload={(fileUpload: IFileUpload, id: string) => {
                                    let currentFormData = { ...formData };
                                    let base64String = btoa(fileUpload.content);
                                    const documentObj: IDocument = {
                                        type: fileConfigObj.documentType,
                                        name: fileUpload.name,
                                        size: fileUpload.size,
                                        content: base64String,
                                        expire: fileConfigObj.expireRequired ? Math.floor(Date.now() / 1000) : null
                                    }
                                    currentFormData.documents[fileConfigObj.id] = documentObj;
                                    setFormData(currentFormData);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                {formData.documents[fileConfigObj.id].content.length > 0 && fileConfigObj.step === formStep && 
                <>
                    <div className="uploadedFileWrapper" style={{paddingLeft:'16px'}}>
                        <InputAttach
                            attachName={formData.documents[fileConfigObj.id].name}
                            firstButtonDisabled={false}
                            secondButtonDisabled={false}
                            inputLabel={t('contract.contractForm.sections.fileInput.attachTitle')}//"File caricato"
                        />
                    </div>

                    {formData.documents[fileConfigObj.id].content.length && fileConfigObj.expireRequired && fileConfigObj.step === formStep &&
                        <Col xs={12} md={3}>
                            <div className="expirationInputWrapper" style={{width:'196px'}}>
                                <DatePicker
                                    id={"scadenzaDoc" + fileConfigObj.id}
                                    datePickerTitle={t('transporter.transporterForm.sections.fileInput.datePickerTitle') + "*"}//"Scadenza*"
                                    datePickerVariant="full-date"
                                    startingYear={new Date().getFullYear()}
                                    endingYear={2099}
                                    defaultDate={new Date(formData.documents[fileConfigObj.id].expire!*1000)} //{new Date()}
                                    disabledDates={{ lastDisabledDate: new Date(Date.now() - 86400000) }} 
                                    isDisabled={false}
                                    datePickerStatus="default"
                                    confirmButtonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                                    hasCloseButton={false}
                                    zIndex={999}
                                    isReadonly={false}
                                    isPanelTop={true}
                                    onDateSelection={(event: Event) => {
                                        const date: string = (event as CustomEvent).detail.dateSelection;
                                        let currentFormData = { ...formData };
                                        currentFormData.documents[fileConfigObj.id].expire = moment.utc(date, DATE_FORMAT).unix();
                                        setFormData(currentFormData);
                                    }}
                                ></DatePicker>
                            </div>
                        </Col>}
                </>}
                </Row>
            </div>)
        });
    }

    const headers: string[] = (() => {
        const mode: string = formMode === FormMode.NEW ? "new" : "edit";
        return t("vehicle.vehicleForm.pageHeaders." + mode, { returnObjects: true }) as string[];
    })();


    return <>
        <p className="pageHeader">
            {headers[formStep < 5 ? 0 : 1]}
        </p>


        <div>
            {formError ?
                <SystemError link={RoutePath.CONTRACTS_VEHICLE_INFO} />
                : <>
                    {/** Stepper - insert mode only */}
                    {formMode === FormMode.NEW && formStep > 0 && <Row>
                        <Col>
                            <Stepper
                                stepperType={StepperType.NEW_VEHICLE}
                                currentStep={formStep}
                                finalStep={5} 
                                formData={formData}
                            />
                        </Col>
                    </Row>}


                    {/** Transporter selector + insert transport button - insert mode only */}
                    {formStep === 0 && <Row>
                        <Col xs={4}>
                            <div className="textInputWrapper">
                                <Select
                                    id="transporterSelector"
                                    menuPlaceholder=""
                                    hasIcon={false}
                                    menuOptions={transporterList}
                                    menuLabel={t('drivers.driverForm.sections.selector.title')}//"Scegli il trasportatore di riferimento"
                                    menuId="select-id"
                                    isDisabled={false}
                                    selectStatus="default"
                                    onOptionSelection={(event: Event) => {
                                        //console.log((event as CustomEvent).detail.option)
                                        const transporter: ISelectLabel = (event as CustomEvent).detail.option;
                                        dsSelectHandler(transporterList, transporter);
                                        let currentFormData: IVehicle = { ...formData };
                                        currentFormData.transporterId = transporter.value!;
                                        setFormData(currentFormData);
                                    }}

                                ></Select>
                            </div>
                            {/** Pulsante inserimento trasportatore */}
                            {appUserProfile?.addEntityPolicy.includes(EntityType.TRANSPORTER) &&
                                <Button
                                    buttonType="button"
                                    buttonUIVariant="secondary"
                                    buttonSize="medium"
                                    buttonLabel={t('general.buttons.addElement', { ns: 'app', what: t('transporter.transporterList.addButton') })}//{"Inserisci trasportatore"}
                                    isDisabled={false}
                                    buttonAriaLabel="Inserisci trasportatore"
                                    buttonIcon="icon-add"
                                    onClickEvent={(event: Event) => {
                                        navigate(RoutePath.CONTRACTS_NEW_TRANSPORT);
                                    }}
                                ></Button>
                            }
                        </Col>
                    </Row >}

                    {/** Step 1 - Data input */}
                    {formStep === 1 && <div className="listTableContainerGrey">
                        <Row>
                            <Col>
                                <p className="formSubTitle">
                                    {/*Inserisci le informazioni del componente*/}
                                    {t('vehicle.vehicleForm.sections.dataInput.title')}
                                </p>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={4}>
                                <div className="textInputWrapper">
                                    <Select
                                        id="type"
                                        menuOptions={vehicleTypeOptions ? vehicleTypeOptions : []}
                                        menuLabel={t('customer.customerForm.sections.info.fields.0')}//"Tipologia"
                                        menuId="type"
                                        isDisabled={formMode === FormMode.EDIT}
                                        selectStatus="default"
                                        onOptionSelection={onVehicleTypeChange}
                                    ></Select>
                                </div>
                            </Col>
                            <Col xs={12} md={4}></Col>
                        </Row>


                        <VehicleFormDataInput
                            formData={formData}
                            onTextInputChange={onTextInputChange}
                            onManufactoringYearInputChange={onManufactoringYearInputChange}
                            onRadioChange={onRadioChange}
                            onLoadingSideChange={onLoadingSideChange}
                        />

                    </div>}

                    {/** Step 2-3-4 files input */}
                    {(formStep >= 2 && formStep <= 4) && <div className="listTableContainerGrey">
                        <Row>
                            <Col>
                                <p className="formSubTitle">
                                    {/*Inserisci la documentazione relativa al componente*/}
                                    {t('vehicle.vehicleForm.sections.fileInput.sectionTitle.' + formStep)}
                                </p>
                            </Col>
                        </Row>
                        {getFileInputElements()}
                    </div>}

                    {/** Step 5 form review */}
                    {formStep === 5 &&
                    
                        <div>
                            <Row>
                                <Col>
                                    <VehicleFormReview formData={formData} formMode={formMode}/>
                                </Col>
                            </Row>
                        </div>
                    }




                </>
            }
        </div>

        {/** Buttons */}
        {!formError && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.8rem" }}>
            {formStep > 0 && <Button
                style={{ marginRight: "0.8rem" }}
                id="backButton"
                buttonType="button"
                buttonUIVariant="secondary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.back', { ns: 'app' })/*formMode === FormMode.NEW ? t('general.buttons.back', { ns: 'app' }) : t('general.buttons.cancel', { ns: 'app' })*/}//"Indietro"
                buttonIcon=""
                isDisabled={false}
                onClickEvent={() => {
                    //In case of truck or floor vehicle type on new mode if I'm on certification loading go to step 2 as there's no procedures loading
                    formMode === FormMode.NEW ?
                        ((formData.type === VehicleType.TRUCK || formData.type === VehicleType.FLOOR) && formStep === 4 ? setFormStep(2) : prevStep()) :
                        (formStep === 5 ? setFormStep(1) : navigate(RoutePath.CONTRACTS_VEHICLE_INFO + "/" + id));
                }}
            />}
            {formStep < 5 && ((formMode === FormMode.NEW /*&& formData.transporterId*/) || formMode === FormMode.EDIT) && <Button
                id="nextButton"
                buttonType="button"
                buttonUIVariant="primary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.next', { ns: 'app' })}//"Continua"
                buttonIcon=""
                isDisabled={formData.transporterId?false:true}
                onClickEvent={() => {
                    let errors: number = validateStep();

                    if (errors === 0) {
                        //In case of truck or floor vehicle type on new mode if I'm on document loading go to step 4 as there's no procedures loading
                        formMode === FormMode.NEW ? ((formData.type === VehicleType.TRUCK || formData.type === VehicleType.FLOOR) && formStep === 2) ? setFormStep(4) : nextStep() : setFormStep(5);

                    }
                }}
            />}
            {formStep === 5 && <Button
                buttonType="button"
                buttonUIVariant="primary"
                buttonSize="medium"
                buttonLabel={t('general.buttons.confirm', { ns: 'app' })}//"Conferma"
                buttonIcon=""
                isDisabled={false}
                onClickEvent={(event: Event) => {
                    //console.log(formData)
                    saveFormData();
                }}
            />}
        </div>}

    </>
}

export default VehicleForm;